import { useMemo, useRef, useState } from "react";
import { varsFromSearch } from "../../../shared/personalize";
import { collectAllTags } from "../../../shared/stepOrchestration";
import { pathScreens, routePath } from "../../../shared/tour";
import Player from "../../player/Player";
import { TabFooter, useEditor } from "../DemoEditor";

export default function PreviewTab() {
  const { bundle } = useEditor();
  const hasSurvey = bundle.demo.surveyEnabled && bundle.questions.length > 0;
  const allTags = useMemo(() => collectAllTags(bundle.questions, bundle.outcomes), [bundle.questions, bundle.outcomes]);
  const pathName = (id: string | null) => (id ? bundle.outcomes.find((o) => o.id === id)?.name ?? "?" : "Default path");

  // "survey" = answer the survey; "default" = the default path; "outcome:<id>" = that path;
  // "tag:<name>" = route as a viewer whose answers gave them that one tag.
  const [mode, setMode] = useState<string>(hasSurvey ? "survey" : "default");
  const [routed, setRouted] = useState<string | null>(null);
  const viewerTags = useRef<string[]>([]);
  const [runKey, setRunKey] = useState(0);
  const [sample, setSample] = useState("");
  const vars = useMemo(() => varsFromSearch(new URLSearchParams(sample.replace(/^\?/, ""))), [sample]);

  const forcedOutcomeId = mode === "default" ? null : mode.startsWith("outcome:") ? mode.slice(8) : undefined;
  const forcedTags = useMemo(() => (mode.startsWith("tag:") ? [mode.slice(4)] : undefined), [mode]);

  // Every count goes through pathScreens and routePath, the same rules the published tour uses.
  const count = (id: string | null) => pathScreens(bundle, id).length;

  return (
    <div className="stack">
      <div className="preview-bar">
        <label className="field" style={{ minWidth: 320 }}>
          <span>Preview as</span>
          <select value={mode} onChange={(e) => { setMode(e.target.value); setRouted(null); setRunKey((k) => k + 1); }}>
            {hasSurvey && <option value="survey">A new viewer (answer the survey)</option>}
            <option value="default">Default path — {count(null)} screens</option>
            {bundle.outcomes.length > 0 && (
              <optgroup label="Paths">
                {bundle.outcomes.map((o) => (
                  <option key={o.id} value={`outcome:${o.id}`}>
                    Path: {o.name} — {count(o.id)} screens
                  </option>
                ))}
              </optgroup>
            )}
            {allTags.length > 0 && (
              <optgroup label="A viewer tagged">
                {allTags.map((tag) => {
                  const to = routePath(bundle.outcomes, [tag]);
                  return <option key={tag} value={`tag:${tag}`}>#{tag} → {pathName(to)} — {count(to)} screens</option>;
                })}
              </optgroup>
            )}
          </select>
        </label>
        <label className="field preview-vars">
          <span>Link values <span className="hint">Fills {"{{variables}}"}</span></span>
          <input type="text" className="mono" placeholder="company=Acme&first_name=Sam" value={sample} onChange={(e) => setSample(e.target.value)} />
        </label>
        <span className="spacer" />
        <button className="btn" onClick={() => { setRouted(null); setRunKey((k) => k + 1); }}>Restart preview</button>
      </div>
      {routed && <div className="route-note">{routed}</div>}
      <Player
        key={`${mode}-${runKey}`}
        bundle={bundle}
        mode="preview"
        forcedOutcomeId={forcedOutcomeId}
        forcedTags={forcedTags}
        vars={vars}
        onRoutedTags={(tags) => { viewerTags.current = tags; }}
        onRouted={(id, matches) => {
          const tags = viewerTags.current.length ? viewerTags.current.map((t) => `#${t}`).join(", ") : "none";
          const detail = bundle.outcomes.map((o) => `${o.name} ${matches[o.id] ?? 0}`).join(", ");
          setRouted(id
            ? `Survey done. Viewer's tags: ${tags}. Routed to ${pathName(id)} (${count(id)} screens), the path matching the most of them. Tags matched: ${detail}.`
            : `Survey done. Viewer's tags: ${tags}. No path matched, so the default path (${count(null)} screens).${detail ? ` Tags matched: ${detail}.` : ""}`);
        }}
      />
      <p className="small muted">Previews aren't counted in analytics and work on drafts.</p>
      <TabFooter back="survey" next="analytics" />
    </div>
  );
}
